import React from 'react';

/**
 * StatusBadge Component
 * 
 * Displays a styled badge indicating status with color coding.
 * Memoized for performance optimization.
 * 
 * @param {string} status - Status value (e.g., 'online', 'offline', 'charging')
 * @param {string} label - Optional custom label to display instead of status
 * @param {string} size - Badge size: 'sm', 'md', 'lg' (default: 'md')
 * @param {string} dataTestId - Test identifier for testing
 * 
 * @example
 * <StatusBadge status="online" size="md" />
 * <StatusBadge status="charging" label="In Progress" />
 */
const StatusBadge = React.memo(({ status, label, size = 'md', dataTestId }) => {
  const getStatusStyles = () => {
    const baseStyles = 'inline-flex items-center gap-1 font-medium rounded-full';
    
    const sizeStyles = {
      xs: 'text-[10px] px-1.5 py-0.5',
      sm: 'text-xs px-2 py-0.5',
      md: 'text-sm px-3 py-1',
      lg: 'text-base px-4 py-1.5',
    };
    
    const colorStyles = {
      up: 'bg-green-100 text-green-800 border border-green-300',
      online: 'bg-green-100 text-green-800 border border-green-300',
      valid: 'bg-green-100 text-green-800 border border-green-300',
      complete: 'bg-green-100 text-green-800 border border-green-300',
      paid: 'bg-green-100 text-green-800 border border-green-300',
      
      occupied: 'bg-blue-100 text-blue-800 border border-blue-300',
      charging: 'bg-blue-100 text-blue-800 border border-blue-300',
      in_progress: 'bg-blue-100 text-blue-800 border border-blue-300',
      
      down: 'bg-red-100 text-red-800 border border-red-300',
      offline: 'bg-red-100 text-red-800 border border-red-300',
      error: 'bg-red-100 text-red-800 border border-red-300',
      expired: 'bg-red-100 text-red-800 border border-red-300',
      
      idle: 'bg-gray-100 text-gray-700 border border-gray-300',
      available: 'bg-gray-100 text-gray-700 border border-gray-300',
      pending: 'bg-gray-100 text-gray-700 border border-gray-300',
      
      at_risk: 'bg-orange-100 text-orange-800 border border-orange-300',
      warning: 'bg-orange-100 text-orange-800 border border-orange-300',
      expiring_soon: 'bg-orange-100 text-orange-800 border border-orange-300',
      
      not_started: 'bg-purple-100 text-purple-800 border border-purple-300',
      info: 'bg-purple-100 text-purple-800 border border-purple-300',
    };
    
    return `${baseStyles} ${sizeStyles[size]} ${colorStyles[status] || 'bg-gray-100 text-gray-700 border border-gray-300'}`;
  };

  const getStatusDot = () => {
    const dotColors = {
      up: 'bg-green-500',
      online: 'bg-green-500',
      valid: 'bg-green-500',
      complete: 'bg-green-500',
      occupied: 'bg-blue-500',
      charging: 'bg-blue-500',
      down: 'bg-red-500',
      offline: 'bg-red-500',
      error: 'bg-red-500',
      idle: 'bg-gray-500',
      available: 'bg-gray-500',
      at_risk: 'bg-orange-500',
      warning: 'bg-orange-500',
    };
    
    const dotClass = dotColors[status] || 'bg-gray-500';
    const dotSize = size === 'xs' ? 'w-1 h-1' : size === 'sm' ? 'w-1.5 h-1.5' : size === 'lg' ? 'w-3 h-3' : 'w-2 h-2';
    
    return <span className={`${dotSize} ${dotClass} rounded-full`} />;
  };

  // Handle null/undefined status safely
  if (!status && !label) {
    return (
      <span className={getStatusStyles()} data-testid={dataTestId}>
        {getStatusDot()}
        N/A
      </span>
    );
  }

  return (
    <span className={getStatusStyles()} data-testid={dataTestId}>
      {getStatusDot()}
      {label || (status ? status.replace(/_/g, ' ').toUpperCase() : 'N/A')}
    </span>
  );
});

// Add display name for debugging
StatusBadge.displayName = 'StatusBadge';

export default StatusBadge;
